import React, { useState } from "react";
import { FormattedMessage } from "react-intl";
import { useOutletContext } from "react-router";
import PropTypes from "prop-types";
import { AdvancedButton } from "components/Buttons";
import {
  isActiveTermSheet,
  isDraftTermSheet,
  isInactiveTermSheet,
  TERM_SHEET_INVERSE_PRICING_TYPES,
  TERM_SHEET_PRICING_TYPES,
  TERM_SHEET_STATES,
} from "constants/index";
import { ICON_TYPE } from "components/Icon";
import { useLegacyTermSheet, useTermSheetContext, useToast } from "hooks";
import { RenameLegacyTermSheetModal } from "components/TermSheets";

/**
 * LegacyTermSheetAdvancedOptions
 *
 *  @params {Object}   termSheet
 */
const LegacyTermSheetAdvancedOptions = ({ termSheet }) => {
  const [showRenameModal, setShowRenameModal] = useState(false);
  const { toast } = useToast();
  const { selectedSquad } = useOutletContext();
  const {
    handleAddTermSheet,
    addingTermSheet,
    handleUpdateTermSheet,
    updatingTermSheet,
    handleRemoveTermSheet,
    removingTermSheet,
  } = useLegacyTermSheet();
  const { refetch, refetching } = useTermSheetContext();
  const { type } = termSheet;

  const activateOption = {
    label: <FormattedMessage id="TermSheets.LegacyTermSheetTable.AdvancedOptions.Activate" />,
    icon: ICON_TYPE.checkDotted,
    onClick: async () => {
      await handleUpdateTermSheet(termSheet?.versionId, { state: TERM_SHEET_STATES.active });
      refetch();
    },
  };

  const deactivateOption = {
    label: <FormattedMessage id="TermSheets.LegacyTermSheetTable.AdvancedOptions.Deactivate" />,
    icon: ICON_TYPE.attention,
    onClick: () => handleUpdateTermSheet(termSheet?.versionId, { state: TERM_SHEET_STATES.inactive }),
  };

  const duplicateOption = {
    label: <FormattedMessage id="TermSheets.LegacyTermSheetTable.AdvancedOptions.Duplicate" />,
    icon: ICON_TYPE.copy,
    onClick: () => handleAddTermSheet(selectedSquad?.id, termSheet?.type, termSheet?.versionId),
  };

  const renameOption = {
    label: <FormattedMessage id="TermSheets.LegacyTermSheetTable.AdvancedOptions.Rename" />,
    icon: ICON_TYPE.edit,
    onClick: () => setShowRenameModal(true),
  };

  const invertedType = TERM_SHEET_INVERSE_PRICING_TYPES[type];
  const switchOption = {
    label: (
      <FormattedMessage
        id="TermSheets.LegacyTermSheetTable.AdvancedOptions.Switch"
        values={{
          type: TERM_SHEET_PRICING_TYPES[invertedType].toLowerCase(),
        }}
      />
    ),
    icon: ICON_TYPE.rotate,
    onClick: () => handleUpdateTermSheet(termSheet?.versionId, { type: invertedType }),
  };

  const deleteOption = {
    label: <FormattedMessage id="TermSheets.LegacyTermSheetTable.AdvancedOptions.Delete" />,
    icon: ICON_TYPE.trash,
    onClick: () => handleRemoveTermSheet(termSheet),
  };

  let options = [];

  if (isInactiveTermSheet(termSheet)) {
    options = [duplicateOption];
  }

  if (isActiveTermSheet(termSheet)) {
    options = [duplicateOption, renameOption, deactivateOption];
  }

  if (isDraftTermSheet(termSheet)) {
    options = [activateOption, duplicateOption, renameOption, switchOption, deleteOption];
  }

  const loading = addingTermSheet || updatingTermSheet || removingTermSheet || refetching;

  const handleRenameModalComplete = () => {
    toast.success(<FormattedMessage id="TermSheets.LegacyTermSheetTable.AdvancedOptions.RenameTermSheet.Success" />);
    setShowRenameModal(false);
  };

  return (
    <>
      {showRenameModal && (
        <RenameLegacyTermSheetModal
          show={showRenameModal}
          name={termSheet?.name}
          versionId={termSheet?.versionId}
          onComplete={handleRenameModalComplete}
          onClose={() => setShowRenameModal(false)}
        />
      )}
      <AdvancedButton loading={loading} options={options} disabled={loading} />
    </>
  );
};

LegacyTermSheetAdvancedOptions.propTypes = {
  termSheet: PropTypes.object,
};

export default LegacyTermSheetAdvancedOptions;
